/*
 * NGE cart drawer: dynamic "Complete your setup" upsells.
 *
 * <cart-upsell-rail> is server-rendered by snippets/cart-upsell.liquid (in the
 * ?view=cart response only) with the fallback collection cards inside it. On
 * connect it asks Shopify's Product Recommendations API for products that go
 * with what's in the cart:
 *   1. complementary (set per product in the Search & Discovery app)
 *   2. related (Shopify's automatic recommendations) to fill remaining slots
 * Results are rendered by sections/cart-upsell-recommendations.liquid using the
 * same card snippet. Recommendations go first; fallback cards fill the rest.
 *
 * Performance:
 * - Responses are cached in memory and sessionStorage (15 min, per market and
 *   currency), so recommendations are fetched once per product per session, not
 *   on every page view or cart change.
 * - When everything is cached the rail is updated synchronously on connect,
 *   before the browser paints, so there is no pop-in.
 * - Nothing is fetched while the drawer is hidden.
 * - On a product page, recommendations for that product start loading as soon as
 *   it is added to the cart, in parallel with the drawer's own refresh.
 */
(() => {
  if (customElements.get('cart-upsell-rail')) return;

  const STORE_KEY = 'nge-cart-upsells:v1';
  const TTL = 15 * 60 * 1000;
  const MAX_ENTRIES = 24;
  const REQUEST_LIMIT = 6;
  const scope = [window.Shopify?.country, window.Shopify?.currency?.active, window.Shopify?.locale].join('|');

  const memory = new Map();
  const pending = new Map();

  try {
    const stored = JSON.parse(sessionStorage.getItem(STORE_KEY));
    if (stored && stored.scope === scope) {
      Object.entries(stored.entries || {}).forEach(([key, entry]) => memory.set(key, entry));
    }
  } catch (error) {
    /* storage unavailable: memory cache only */
  }

  const persist = () => {
    try {
      const entries = Array.from(memory.entries())
        .sort((a, b) => b[1].t - a[1].t)
        .slice(0, MAX_ENTRIES);
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ scope, entries: Object.fromEntries(entries) }));
    } catch (error) {
      /* quota or privacy mode: ignore */
    }
  };

  const parse = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('[data-upsell-product-id]'), (el) => ({
      id: el.dataset.upsellProductId,
      handles: (el.dataset.upsellHandles || '').split(',').filter(Boolean),
      html: el.outerHTML,
    }));
  };

  // Returns the cached array synchronously, or a promise when a request is needed.
  const load = (baseUrl, sectionId, productId, intent) => {
    const key = `${productId}:${intent}`;
    const hit = memory.get(key);
    if (hit && Date.now() - hit.t < TTL) return hit.items;
    if (pending.has(key)) return pending.get(key);

    const url =
      `${baseUrl}?product_id=${encodeURIComponent(productId)}` +
      `&limit=${REQUEST_LIMIT}&intent=${intent}&section_id=${encodeURIComponent(sectionId)}`;

    const request = fetch(url, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        const items = parse(html);
        memory.set(key, { t: Date.now(), items });
        persist();
        return items;
      })
      .catch((error) => {
        console.warn('[cart-upsell]', error);
        return [];
      })
      .finally(() => pending.delete(key));

    pending.set(key, request);
    return request;
  };

  // Resolve synchronously when every value is already an array.
  const whenAll = (values, callback) => {
    if (values.every(Array.isArray)) return callback(values);
    return Promise.all(values).then(callback);
  };

  // Round-robin across anchor products so the most recently added item leads
  // but every item in the cart gets representation.
  const interleave = (lists) => {
    const out = [];
    const longest = Math.max(0, ...lists.map((list) => list.length));
    for (let i = 0; i < longest; i += 1) {
      lists.forEach((list) => {
        if (list[i]) out.push(list[i]);
      });
    }
    return out;
  };

  const splitList = (value) => (value || '').split(',').filter(Boolean);

  class CartUpsellRail extends HTMLElement {
    connectedCallback() {
      if (this.dataset.dynamic !== 'true' || this._started) return;
      // Hidden drawer: it is re-rendered when opened, so do nothing now.
      if (!this.getClientRects().length) return;

      const anchors = splitList(this.dataset.anchors);
      if (!anchors.length) return;
      this._started = true;

      const { url, sectionId } = this.dataset;
      const limit = parseInt(this.dataset.limit, 10) || 8;
      const exclude = new Set(splitList(this.dataset.exclude));
      const excludeHandles = new Set(splitList(this.dataset.excludeHandles));
      // Combined listing parents share no product ID with the child in the cart.
      const inCart = (handles) => handles.some((handle) => excludeHandles.has(handle));

      // candidates: complementary for every anchor first, then related.
      const pick = (candidates) => {
        const seen = new Set(exclude);
        const picks = [];
        for (const item of candidates) {
          if (picks.length >= limit) break;
          if (seen.has(item.id) || inCart(item.handles)) continue;
          seen.add(item.id);
          picks.push(item);
        }
        return picks;
      };

      // Both intents are requested together: complementary rarely fills the rail
      // on its own, so waiting for it before asking for related costs a round trip.
      const requests = [
        ...anchors.map((id) => load(url, sectionId, id, 'complementary')),
        ...anchors.map((id) => load(url, sectionId, id, 'related')),
      ];

      whenAll(requests, (lists) => {
        const complementary = interleave(lists.slice(0, anchors.length));
        const related = interleave(lists.slice(anchors.length));
        this.apply(pick([...complementary, ...related]), limit, exclude, inCart);
      });
    }

    apply(picks, limit, exclude, inCart) {
      if (!this.isConnected) return;

      if (picks.length) {
        const pickIds = new Set(picks.map((item) => item.id));
        let slotsLeft = limit - picks.length;

        // Keep fallback cards in place (no re-init) and trim duplicates/overflow.
        Array.from(this.querySelectorAll(':scope > [data-upsell-product-id]')).forEach((el) => {
          const id = el.dataset.upsellProductId;
          if (pickIds.has(id) || exclude.has(id) || inCart(splitList(el.dataset.upsellHandles)) || slotsLeft <= 0) {
            el.remove();
          } else {
            slotsLeft -= 1;
          }
        });

        const template = document.createElement('template');
        template.innerHTML = picks.map((item) => item.html).join('');
        this.prepend(template.content);
        this.scrollLeft = 0;
      }

      const section = this.closest('.cart-upsell');
      if (section) section.hidden = this.children.length === 0;
      this.dispatchEvent(new CustomEvent('cart-upsells-rendered', { bubbles: true }));
    }
  }

  customElements.define('cart-upsell-rail', CartUpsellRail);

  // Product page: the product just added is the most likely new anchor, so start
  // its requests alongside the drawer's ?view=cart refresh instead of after it.
  const config = window.cartUpsellConfig;
  const productId = window.ShopifyAnalytics?.meta?.product?.id;
  if (config?.dynamic && productId) {
    window.addEventListener('cart-updated', () => {
      load(config.url, config.sectionId, String(productId), 'complementary');
      load(config.url, config.sectionId, String(productId), 'related');
    });
  }
})();
